import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';

import {
  SortableList, SortableListRow, keyOf, reorderById, reorderInState, withDndSensors,
} from './shared/dndSortable';
import { newColumn, processInitialColumns } from './shared/columnFields';
import Column from './classificationFields/Column';

export { reorderById };

class ClassificationFields extends React.Component {
  static propTypes = {
    classificationName: PropTypes.string.isRequired,
    showSchemaField: PropTypes.bool.isRequired,
    initialKeyColumns: PropTypes.array,
    initialDataColumns: PropTypes.array,
    availableColumns: PropTypes.array.isRequired,
    disabled: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      availableColumns: processInitialColumns(this.props.availableColumns, this.columnId, newColumn),
      keyColumns: processInitialColumns(this.props.initialKeyColumns, this.columnId, newColumn),
      dataColumns: processInitialColumns(this.props.initialDataColumns, this.columnId, newColumn),
    };
  }

  columnId = (column) => `${column.columnType}-${column.name}`;

  onDragEnd = (event) => reorderInState(event, this, 'dataColumns');

  handleSchemaChange = (event) => {
    const detectedHeaders = event.target.value.split(',');
    const keyHeader = detectedHeaders.shift();

    const newKeyColumns = [];
    newKeyColumns.push(this.columnForHeader(keyHeader));

    const newDataColumns = [];
    for (const header of detectedHeaders) {
      newDataColumns.push(this.columnForHeader(header));
    }

    this.setState({ keyColumns: newKeyColumns, dataColumns: newDataColumns });
  };

  columnForHeader = (header) => {
    const column = newColumn();
    column.header = header;
    const validColumn = this.props.availableColumns.find((c) => c.gaName === header);
    if (validColumn) {
      column.id = validColumn.id;
      column.columnType = validColumn.columnType;
      column.name = validColumn.name;
    }
    return column;
  };

  handleNameChange = (index, value, columns, listName) => {
    const validColumn = this.state.availableColumns.find((c) => c.id === value);
    if (validColumn) {
      const newDataColumns = update(columns, {
        [index]: {
          id: { $set: validColumn.id },
          columnType: { $set: validColumn.columnType },
          name: { $set: validColumn.name },
        },
      });
      this.setState({ [listName]: newDataColumns });
    }
  };

  handleKeyNameChange = (index, value) =>
    this.handleNameChange(index, value, this.state.keyColumns, 'keyColumns');

  handleDataNameChange = (index, value) =>
    this.handleNameChange(index, value, this.state.dataColumns, 'dataColumns');

  handleKeyHeaderChange = (index, value) => {
    const newColumns = update(this.state.keyColumns, { [index]: { header: { $set: value } } });
    this.setState({ keyColumns: newColumns });
  };

  handleDataHeaderChange = (index, value) => {
    const newColumns = update(this.state.dataColumns, { [index]: { header: { $set: value } } });
    this.setState({ dataColumns: newColumns });
  };

  handleKeyColumnRemove = (index) => {
    const newColumns = update(this.state.keyColumns, { $splice: [[index, 1]] });
    this.setState({ keyColumns: newColumns });
  };

  handleDataColumnRemove = (index) => {
    const newColumns = update(this.state.dataColumns, { $splice: [[index, 1]] });
    this.setState({ dataColumns: newColumns });
  };

  handleKeyAddColumn = () => {
    const newColumns = update(this.state.keyColumns, { $push: [newColumn()] });
    this.setState({ keyColumns: newColumns });
  };

  handleDataAddColumn = () => {
    const newColumns = update(this.state.dataColumns, { $push: [newColumn()] });
    this.setState({ dataColumns: newColumns });
  };

  showRemoveIcon = (list) => list.length > 1;

  render() {
    const itemIds = this.state.dataColumns.map(keyOf);

    let schemaNode;
    if (this.props.showSchemaField) {
      schemaNode = (
        <div className="form-group">
          <label className="control-label col-xs-2">Data Set Schema (Optional)</label>
          <div className="col-xs-1 col-drag-handle"></div>
          <div className="col-xs-6">
            <input type="text" defaultValue="" onChange={this.handleSchemaChange} className="form-control" />
            <span className="help-block text-muted">
              This is the CSV header after you click 'Get Schema' for a given Data Set in Google Analytics Data Import section.
            </span>
          </div>
        </div>
      );
    }

    return (
      <div>
        {schemaNode}

        <div className="form-group">
          <div className="col-xs-10 col-xs-offset-2">
            <div className="row">
              <div className="col-xs-1 col-drag-handle"></div>
              <div className="col-xs-10">
                <span className="text-semibold">
                  Specify the mapping between UTM/Custom parameters/Info fields and column headings
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="control-label col-xs-2">
            <span>
              Key Column
              <a className="tool-tip" data-toggle="tooltip" data-placement="top" data-trigger="hover" title="Often utm_id or cid">
                <i className="icon-info22 text-muted text-size-small position-right"></i>
              </a>
            </span>
          </label>

          <div className="col-xs-10">
            {this.state.keyColumns.map((column, index) => (
              <div className="row mb-10" key={column.key}>
                <div className="col-xs-1 col-drag-handle"></div>
                <Column
                  index={index}
                  column={column}
                  baseInputName={`${this.props.classificationName}[key_columns]`}
                  availableColumns={this.props.availableColumns}
                  handleNameChange={this.handleKeyNameChange}
                  handleHeaderChange={this.handleKeyHeaderChange}
                  onRemove={this.handleKeyColumnRemove}
                  showRemoveIcon={this.showRemoveIcon(this.state.keyColumns)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="control-label col-xs-2"><span>Data Columns</span></label>

          <div className="col-xs-10">
            <SortableList sensors={this.props.__sensors} items={itemIds} onDragEnd={this.onDragEnd}>
              {this.state.dataColumns.map((column, index) => (
                <SortableListRow key={column.key} id={keyOf(column)}>
                  <Column
                    index={index}
                    column={column}
                    baseInputName={`${this.props.classificationName}[data_columns]`}
                    availableColumns={this.state.availableColumns}
                    handleNameChange={this.handleDataNameChange}
                    handleHeaderChange={this.handleDataHeaderChange}
                    onRemove={this.handleDataColumnRemove}
                    showRemoveIcon={this.showRemoveIcon(this.state.dataColumns)}
                  />
                </SortableListRow>
              ))}
            </SortableList>

            <div className="row">
              <div className="col-xs-1 col-drag-handle"></div>
              <div className="col-xs-11">
                <a onClick={this.handleDataAddColumn}>
                  <i className="icon-plus22 position-left"></i>
                  Add column
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withDndSensors(ClassificationFields);
